import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseComponent, WmsDialogComponent, WmsDialogHeaderComponent, WmsDialogBodyComponent, WmsDialogFooterComponent, WmsMessageComponent } from '@wms/components';
import { FormsModule } from '@angular/forms';
import { CONSTANTS, getActionFromKey, StudioUtils } from '@wms/core';
import { forEach } from 'lodash-es';
import { NotificationService, PageService, PrefabService } from '@wms/services';
import { BaseVariableService } from '@wms/variables';
import { PageWsService } from '@wms/workspace';
import { AppResourceManagerService } from "@wms/design-container";
import { Router } from '@angular/router';

@Component({
  selector: 'wms-rename-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    WmsDialogComponent,
    WmsDialogHeaderComponent,
    WmsDialogBodyComponent,
    WmsDialogFooterComponent,
    WmsMessageComponent
  ],
  templateUrl: './rename-page.component.html',
  styleUrls: ['./rename-page.component.less']
})
export class RenamePageComponent extends BaseComponent implements OnInit {

  @Input() dialogParams: any;
  @Output() onModalClose: EventEmitter<any> = new EventEmitter<any>();

  pageName: any;
  fileAddMessage: any;
  dialogCaption: any;
  dialogContent: any;
  newPage: any;
  oldPageName: any;
  dialogAction: any;
  pageNameLimit: any;
  template: any;
  renameMessage: any;

  private pageService: PageService;
  private prefabService: PrefabService;
  private baseVariableService: BaseVariableService;
  private notificationService: NotificationService;
  private pageWsService: PageWsService;
  private router: Router;

  constructor(private resourceMngr: AppResourceManagerService) {
    super();
    this.pageService = inject(PageService);
    this.prefabService = inject(PrefabService);
    this.baseVariableService = inject(BaseVariableService);
    this.notificationService = inject(NotificationService);
    this.pageWsService = inject(PageWsService);
    this.router = inject(Router);
  }

  ngOnInit(): void {
    this.fileAddMessage = null;
    this.renameMessage = null;
    this.dialogCaption = this.dialogParams.dialogCaption;
    this.dialogContent = this.dialogParams.dialogContent;
    this.oldPageName = this.dialogParams.pageName;
    this.dialogAction = this.dialogParams.action;
    this.pageNameLimit = CONSTANTS.CHARACTERS_LIMIT.PAGE_NAME;

    if (this.dialogParams.action === 'RENAME') {
      this.pageName = this.dialogParams.pageName;
      this.renameMessage = {
        'caption': this.locale.LABEL_CONFIRM_RENAME_PAGE_INFO,
        'type': 'info'
      };
    } else {
      this.generateDuplicatedName();
    }
  }

  /**
   * This function checks if the page name length is greater than the page name limit,
   * If so it removes the  extra characters from the end of page name so as to accomodate
   * the page name along with post tag in the specified page name limit
   */
  generateDuplicatedName() {
    let POSTTAG = "_Copy",
      charInPageName = this.dialogParams.pageName.length,
      extraCharInPageName = charInPageName + POSTTAG.length - this.pageNameLimit,
      tempName = this.dialogParams.pageName;
    if (extraCharInPageName > 0) {
      tempName = this.dialogParams.pageName.substring(0, charInPageName - extraCharInPageName);
    }
    this.pageName = tempName + POSTTAG;
  }

  getConfigData(config: any) {
    if (!config) {
      return config;
    } else {
      forEach(config.templates, (template: any) => {
        if (template.name === this.oldPageName) {
          this.template = template;
          return;
        }
      })
    }
  }

  /**
   * This function handles the renaming of the template in the config
   * @param config, template config
   * @param newTemplateName, renamed template name
   * @param oldTemplateName, old template name
   * @param callback, The callback function to be called on success of saveconfig
   */
  templateRename(config: any, newTemplateName: any, oldTemplateName: any, callback?: any) {
    forEach(config.templates, (template: any) => {
      if (template.name === oldTemplateName) {
        template.name = newTemplateName;
        template.id = newTemplateName;
        return;
      }
    });
    //StudioUtils.saveConfig("renamePageDialog", config, callback);
  }

  /**
   * Reloads the Page
   */
  reloadPage(pageName: string) {
    this.router.navigate(['page/' + pageName]);
    setTimeout(() => {
      window.location.reload();
    });
  }

  /**
   * rename/duplicate a page
   */
  renameDuplicatePage = () => {
    if (this.pageWsService.isDirty()) {
      (this.pageWsService.saveActiveTabContent() as Promise<any>).then((config: any) => {
        this.renameOrDuplicatePage();
      }).catch((error: any) => {
        this.saveUnsuccesful(error);
      });
    }
    else {
      this.renameOrDuplicatePage();
    }
  };

  onPageRenameSuccess() {
    let newWS = {
      'name': this.pageName,
      'type': 'PAGE',
      'identifier': 'PAGE:' + this.pageName,
      'details': { 'pageName': this.pageName, 'tab': 'DESIGN' }
    }, oldWS = {
      'name': this.oldPageName,
      'type': 'PAGE',
      'identifier': 'PAGE:' + this.oldPageName,
      'details': { 'pageName': this.oldPageName, 'tab': 'DESIGN' }
    };
    this.fileAddMessage = { 'caption': this.locale.LABEL_CONFIRM_RENAME_SUCCESSFUL_MESSAGE, type: 'success' };
    this.resourceMngr.removeUrlCache(this.oldPageName);
    this.eventManager.emitupdateRouteHistory({name: this.oldPageName, type: "PAGE"});
    //WSList.rename(oldWS, newWS);
    // If a template is renamed, the template config needs to be updated
    if (this.projectService.isTemplateBundleType()) {
      (this.prefabService.getConfig() as Promise<any>).then((config: any) => {
        this.closeModal();
        this.templateRename(config, this.pageName, this.oldPageName, this.reloadPage(this.pageName));
      });
    } else {
      this.closeModal();
      this.reloadPage(this.pageName);
    }
  }

  onPageDuplicateSuccess() {
    this.newPage = {
      type: this.dialogParams.pageType,
      name: this.pageName
    };
    let gotoVarName = "goToPage_" + (this.dialogParams.pageName), goToVar = this.baseVariableService?.variableCollection['App'][gotoVarName], dataset;
    if (goToVar) {
      dataset = goToVar.dataSet;
    }
    StudioUtils.onPageCreationSuccess(this.pageName, 'renamePageDialog', this, 'DUPLICATE', dataset);
    this.notificationService.show("success", this.locale.MESSAGE_SUCCESS_TITLE, this.locale.MESSAGE_SUCCESS_PAGE_DUPLICATE.replace("${0}", this.pageName));
  }


  onTemplateDuplicationSuccess() {
    let ADD = 1;
    this.newPage = {
      type: this.dialogParams.pageType,
      name: this.pageName
    };
    // StudioUtils.onPageOrTemplateCreateSuccess(this, this.pageName, addNewWorkspace);
    StudioUtils.onPageOrTemplateCreateSuccess(this, this.pageName);
    StudioUtils.updateTemplateConfig(ADD, this.pageName, 'renamePageDialog', this);
  }

  onPageRenameError(errMsg: any) {
    this.fileAddMessage = { 'caption': errMsg, type: 'error' };
  }

  saveUnsuccesful(errMsg: any) {
    this.fileAddMessage = { 'caption': errMsg, type: 'error' };
  }

  closeModal() {
    this.onModalClose.emit();
  }

  renameOrDuplicatePage() {
    let requestBodyData: any;
    if (this.dialogParams.action === 'RENAME') {
      if (StudioUtils.validatePageName(this, this.pageName)) {
        this.fileAddMessage = {
          'caption': this.locale.LABEL_CONFIRM_RENAME_MESSAGE,
          type: 'loading'
        };
        requestBodyData = { 'newPageName': this.pageName };
        (this.pageService.rename(this.dialogParams.pageName, requestBodyData) as Promise<any>).then((res: any) => {
          this.onPageRenameSuccess();
        }).catch((error: any) => {
          this.onPageRenameError(error);
        });
      }
    } else {
      if (StudioUtils.validatePageName(this, this.pageName)) {
        this.fileAddMessage = { 'caption': this.locale.LABEL_CONFIRM_DUPLICATE_MESSAGE, type: 'loading' };
        requestBodyData = { 'copyPageName': this.pageName };
        if (this.projectService.isTemplateBundleType()) {
          (this.prefabService.getConfig() as Promise<any>).then((response: any) => {
            this.getConfigData(response);
          });
        }
        (this.pageService.duplicate(this.dialogParams.pageName, requestBodyData) as Promise<any>).then((response: any) => {
          if (this.projectService.isTemplateBundleType()) {
            this.onTemplateDuplicationSuccess();
          } else {
            this.onPageDuplicateSuccess();
          }
        }).catch((error: any) => {
          this.onPageRenameError(error);
        });
      }
    }
  }

  /*method to handle rename/duplicte page with enter key submit*/
  initiateRenameOrDuplicate = ($event: any) => {
    $event.stopPropagation();
    if (getActionFromKey($event) === 'ENTER') {
      /*import project if enter key pressed*/
      this.renameDuplicatePage();
    }
  };
}

